import { Platform } from '@angular/cdk/platform';
import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';

export enum DeviceCategory {
  DESKTOP = 'Desktop',
  MOBILE = 'Mobile',
  TABLET = 'Tablet',
}

@Injectable({
  providedIn: 'root',
})
export class DeviceAndBrowserDetectorService {
  private audioElementToCheckSetSinkId?: HTMLAudioElement;

  constructor(private deviceDetectorService: DeviceDetectorService, private platform: Platform) {}

  get browser(): string {
    return this.deviceDetectorService.browser;
  }

  /**
   * Returns the current device type
   * @returns DeviceCategory enum value (DESKTOP, MOBILE, or TABLET)
   */
  getCurrentDeviceType(): DeviceCategory {
    if (this.isTabletDevice()) {
      return DeviceCategory.TABLET;
    } else if (this.isMobile()) {
      return DeviceCategory.MOBILE;
    } else {
      return DeviceCategory.DESKTOP;
    }
  }

  /**
   * Returns the current browser name
   * @returns Browser name as detected by the device detector service
   */
  getCurrentBrowser(): string {
    return this.deviceDetectorService.browser;
  }

  /**
   * Returns the current user agent string
   * @returns User agent string from navigator
   */
  getUserAgent(): string {
    return navigator.userAgent;
  }

  isMobile(): boolean {
    return this.deviceDetectorService.isMobile() && !this.isTabletDevice();
  }

  // Browsers detection
  isFirefox(): boolean {
    if (/firefox|fxios/i.test(navigator.userAgent)) {
      return true;
    }
    // iPad defaults to a desktop Safari-like UA without FxiOS (firefox-ios#25065).
    return this.isFirefoxOnIPadDesktopSpoofedUserAgent();
  }

  // this check not enough to detect chrome browser
  // return true for chromium based browsers like edge, opera, etc.
  isChrome(): boolean {
    return (
      window.navigator.userAgent.toLowerCase().indexOf('chrome') > -1 ||
      /CriOS/i.test(navigator.userAgent)
    );
  }

  // chrome version above 90 support userAgentData brands
  isChromeBrowserAbove90(): boolean {
    return !!window.navigator.userAgentData?.brands.some((x) => x.brand === 'Google Chrome');
  }

  isEdge(): boolean {
    return window.navigator.userAgent.toLowerCase().indexOf('edg') > -1;
  }

  isOpera(): boolean {
    return window.navigator.userAgent.toLowerCase().indexOf('OP') > -1;
  }

  isSafari(): boolean {
    // All below checks are added to detect real Safari as user agent contains Safari for all browsers
    return (
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
      !/CriOS/i.test(navigator.userAgent) &&
      !/firefox|fxios|Firefox|Fxios/i.test(navigator.userAgent) &&
      !/EdgiOS/i.test(navigator.userAgent)
    );
  }

  getSafariMajorVersion(): number | null {
    if (!this.isSafari()) {
      return null;
    }
    const match = navigator.userAgent.match(/Version\/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  getIOSMajorVersion(): number | null {
    if (!this.isiOS()) {
      return null;
    }
    // Matches "iPhone OS 18_0" or "CPU OS 18_0" patterns in the UA string (iPhone, and Chrome/non-Safari on iPad)
    const match = navigator.userAgent.match(/(?:iPhone OS|CPU OS) (\d+)/);
    if (match) {
      return parseInt(match[1], 10);
    }
    // iPad on iOS 13+ reports as "Macintosh" — OS version is hidden, but "Version/X" reflects iPadOS version (Safari on iPad)
    const versionMatch = navigator.userAgent.match(/Version\/(\d+)/);
    return versionMatch ? parseInt(versionMatch[1], 10) : null;
  }

  isSetSinkIdSupported(): boolean {
    if (!this.audioElementToCheckSetSinkId) {
      this.audioElementToCheckSetSinkId = document.createElement('audio');
    }
    return !this.isSafari() && !this.isiOS() && 'setSinkId' in this.audioElementToCheckSetSinkId;
  }

  // Device & OS detection
  isDesktop(): boolean {
    return this.deviceDetectorService.isDesktop() && !this.isTabletDevice();
  }

  isiOSiPhone(): boolean {
    return this.platform.IOS && this.deviceDetectorService.isMobile();
  }

  isiOSiPad(): boolean {
    return (
      (this.platform.IOS ||
        // iPad on > iOS 13 detection
        this.isIPadWithIOS13OrMore()) &&
      (this.deviceDetectorService.isTablet() || this.isTabletDevice())
    );
  }

  isAndroidPhone(): boolean {
    return this.platform.ANDROID && this.deviceDetectorService.isMobile();
  }

  isAndroidTablet(): boolean {
    return (
      this.platform.ANDROID && (this.deviceDetectorService.isTablet() || this.isTabletDevice())
    );
  }

  isiOS(): boolean {
    return this.platform.IOS || this.isIPadWithIOS13OrMore();
  }

  isAndroid(): boolean {
    return this.platform.ANDROID;
  }

  isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  isWindows(): boolean {
    return !!navigator.userAgent.match(/(Windows)/i);
  }

  isMacOS(): boolean {
    return !!navigator.userAgent.match(/(Mac)/i) && this.isDesktop();
  }

  isAppleDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform?.toLowerCase() || '';

    return /iphone|ipad|ipod|macintosh/.test(userAgent) || platform.startsWith('mac');
  }

  isTabletDevice(): boolean {
    const regex =
      /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/;
    return (
      regex.test(navigator.userAgent.toLowerCase()) ||
      // iPad on > iOS 13 detection
      this.isIPadWithIOS13OrMore()
    );
  }

  isTouchDevice(): boolean {
    return this.isMobile() || this.isTabletDevice() || this.isiOSiPad();
  }

  /**
   * Returns true when running Firefox on a non-desktop device (mobile or tablet).
   */
  isFirefoxOnMobileOrTabletDevice(): boolean {
    return this.isFirefox() && !this.isDesktop();
  }

  isIPadWithIOS13OrMore(): boolean {
    return (
      navigator.userAgent.includes('Macintosh') &&
      // Filter non-touch devices (Mac)
      'ontouchend' in document
    );
  }

  /**
   * Firefox on iPad sends a desktop Macintosh + Safari UA without FxiOS; Chrome/Edge keep CriOS/EdgiOS.
   */
  private isFirefoxOnIPadDesktopSpoofedUserAgent(): boolean {
    if (!this.isIPadWithIOS13OrMore() || this.isSafari()) {
      return false;
    }
    const ua = navigator.userAgent;
    if (/CriOS|EdgiOS|fxios|firefox/i.test(ua)) {
      return false;
    }
    return /Macintosh/i.test(ua) && /Safari/i.test(ua);
  }

  // ---- Browser Feature Support ----

  public supportElementCapture() {
    return window.RestrictionTarget !== undefined;
  }

  public supportCaptureHandleConfig() {
    return navigator.mediaDevices.setCaptureHandleConfig !== undefined;
  }
}

/**
 * Note:
 * Types for some of the newer chrome APIs are not included in the standard @types/chrome-types
 * so we have to add them here. Ensure that any extension of existing browser apis are all made optional
 * so that the callee must check to see if they exist before using them.
 */
declare global {
  interface Window {
    RestrictionTarget?: {
      fromElement: (element: HTMLElement) => Promise<RestrictionTarget>;
    };

    /**
     * This is the window object of the pip window.
     * It is used to detect the size of the pip window.
     * It is not available in the main window.
     */
    pipWindow?: Window;

    documentPictureInPicture?: {
      requestWindow: () => Promise<Window>;
      window?: Window;
    };
  }

  interface Navigator {
    userAgentData?: {
      brands: { brand: string; version: string }[];
    };
  }

  interface MediaStreamTrack {
    getCaptureHandle?: () => { handle: string } | null;
    restrictTo?: (target: RestrictionTarget | null) => Promise<void>;
    oncapturehandlechange?: () => void;
  }

  interface RestrictionTarget {}

  interface MediaDevices {
    setCaptureHandleConfig?: (config: { handle: string; permittedOrigins: string[] }) => void;
  }
}

export const videoCallWindow = (): Window => window.pipWindow || window;
